import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Box } from "lucide-react";
import { MaskLines } from "@/components/MaskLines";
import { useLang } from "@/lib/i18n";

const Badge = () => {
  const { t } = useLang();
  return (
  <Link to="/explorar?mode=3d" className="group absolute bottom-10 right-6 z-10 hidden h-36 w-36 md:block lg:right-12" data-testid="hero-badge">
    <svg viewBox="0 0 100 100" className="spin-slow h-full w-full">
      <defs><path id="c" d="M50 50 m-38 0 a38 38 0 1 1 76 0 a38 38 0 1 1 -76 0" /></defs>
      <text fill="#efe8dc" style={{ font: "500 8.4px 'JetBrains Mono', monospace", letterSpacing: "2.6px" }}>
        <textPath href="#c">{t("hero.badge")}</textPath>
      </text>
    </svg>
    <span className="absolute inset-0 m-auto grid h-14 w-14 place-items-center rounded-full bg-[var(--green)] text-[var(--bg)] transition-transform duration-500 group-hover:scale-110">
      <Box size={22} />
    </span>
  </Link>
  );
};

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.22]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 60, damping: 18 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="relative flex min-h-[100svh] items-end overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0" style={{ perspective: 1200 }}>
        <motion.div
          className="absolute inset-[-5%]"
          style={{ rotateX: rx, rotateY: ry, y }}
          initial={{ clipPath: "circle(0% at 50% 48%)" }}
          animate={{ clipPath: "circle(85% at 50% 48%)" }}
          transition={{ duration: 2.2, ease: [0.65, 0, 0.35, 1], delay: 0.15 }}
        >
          <motion.img src="/img/hallway.webp" alt={t("hero.alt")} style={{ scale }} className="h-full w-full object-cover" />
        </motion.div>
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-[rgba(13,12,10,0.55)] to-[rgba(13,12,10,0.35)]" />
      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-5 pb-16 pt-32 md:px-10 md:pb-20">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="mono text-xs text-[var(--brick-light)]" data-testid="hero-kicker">
          {t("hero.kicker")}
        </motion.p>
        <MaskLines
          as="h1"
          delay={0.9}
          className="mt-5 max-w-5xl font-display text-[3.2rem] leading-[0.92] sm:text-7xl lg:text-[8rem]"
          lines={[t("hero.l1"), t("hero.l2"), <em key="e" className="text-[var(--green)]">{t("hero.l3")}</em>]}
        />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.9 }} className="mt-8 flex max-w-xl flex-col gap-6">
          <p className="text-base text-[var(--ink)]/90 md:text-lg">{t("hero.body")}</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/explorar?mode=3d" className="btn-primary" data-testid="hero-cta-3d"><Box size={17} /> {t("hero.cta3d")}</Link>
            <Link to="/explorar" className="btn-ghost" data-testid="hero-cta-plan">{t("hero.ctaPlan")} <ArrowDownRight size={17} /></Link>
          </div>
        </motion.div>
      </div>
      <Badge />
    </section>
  );
};
